/**
 * /admin/home-blocks — التحكّم بإظهار/إخفاء وترتيب بلوكات Collections في الهوم.
 *
 * - مصدر القائمة: Shopify Collections (تلقائيّ) + البلوكات الداخليّة.
 * - تابان: «ديسكتوب» و«موبايل» — لكلّ جهاز ترتيب مستقلّ.
 * - الإظهار/الإخفاء مستقلّ لكلّ جهاز.
 * - تحفظ الإعداد في app_settings.home_collections_config
 *   = { hidden, hidden_desktop, hidden_mobile, order, desktop_order, mobile_order }.
 * - زرّ حفظ صريح يضيء عند التعديل (draft + isDirty).
 */
import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowUp, ArrowDown, Loader2, Save, RotateCcw, Monitor, Smartphone, ImageIcon, X, UploadCloud, AlertTriangle } from "lucide-react";
import { toast } from "@/lib/toast";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchCollections } from "@/lib/shopify";
import {
  getHomeCollectionsConfig,
  setHomeCollectionsConfig,
  deleteHomeBlockMedia,
  type HomeCollectionsConfig,
  type HomeBlockMediaOverride,
} from "@/lib/admin/homeCollectionsConfig.functions";
import { useAdminGate } from "@/hooks/useAdminGate";
import {
  HOME_BLOCK_FEATURED_PRODUCTS,
  HOME_BLOCK_INFO_PRIMARY,
  HOME_BLOCK_INFO_SECONDARY,
  VIRTUAL_HOME_BLOCK_LABELS,
  VIRTUAL_HOME_BLOCK_HINTS,
} from "@/lib/admin/home-blocks-constants";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { compressImageBeforeUpload } from "@/lib/compress-image";
import { bustUrl } from "@/lib/storage-bust";

const VIRTUAL_HANDLES = [
  HOME_BLOCK_FEATURED_PRODUCTS,
  HOME_BLOCK_INFO_PRIMARY,
  HOME_BLOCK_INFO_SECONDARY,
] as const;

type Device = "desktop" | "mobile";

export const Route = createFileRoute("/admin/home-blocks")({
  validateSearch: (search: Record<string, unknown>): { device?: Device; edit?: string } => {
    const device = search.device === "desktop" || search.device === "mobile" ? search.device : undefined;
    return {
      device,
      edit: typeof search.edit === "string" ? search.edit : undefined,
    };
  },
  component: AdminHomeBlocksPage,
  head: () => ({
    meta: [
      { title: "Home Blocks · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function mergeOrder(saved: string[], all: string[]): string[] {
  const inAll = saved.filter((h) => all.includes(h));
  const missing = all.filter((h) => !inAll.includes(h));
  return [...inAll, ...missing];
}

/** يجمع كلّ روابط الوسائط المستخدَمة في overrides لمقارنة الحذف. */
function collectMediaUrls(
  overrides: Record<string, HomeBlockMediaOverride>,
): Set<string> {
  const urls = new Set<string>();
  for (const ov of Object.values(overrides)) {
    if (ov?.desktop_image_url) urls.add(ov.desktop_image_url);
    if (ov?.mobile_image_url) urls.add(ov.mobile_image_url);
  }
  return urls;
}

function AdminHomeBlocksPage() {
  const { ready } = useAdminGate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const getConfig = useServerFn(getHomeCollectionsConfig);
  const saveConfig = useServerFn(setHomeCollectionsConfig);
  const deleteMedia = useServerFn(deleteHomeBlockMedia);

  const { data: collections, isLoading: cLoading } = useQuery({
    queryKey: ["admin", "shopify-collections-all"],
    queryFn: () => fetchCollections(50),
    staleTime: 60_000,
  });

  const { data: config, isLoading: gLoading } = useQuery({
    queryKey: ["home-collections-config", "admin"],
    queryFn: () => getConfig(),
    enabled: ready,
    staleTime: 30_000,
  });

  const { t } = useLocale();
  const [draft, setDraft] = React.useState<HomeCollectionsConfig | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [device, setDevice] = React.useState<Device>("desktop");
  const [editingMediaHandle, setEditingMediaHandle] = React.useState<string | null>(null);
  const openedSearchEditRef = React.useRef<string | null>(null);

  /**
   * الجهاز الفعّال فعلاً في الواجهة: الموبايل فقط.
   * بلوكات المجموعات غير مركَّبة في الصفحة الرئيسيّة على الديسكتوب،
   * لذلك نعطّل تفاعل تاب الديسكتوب بدل إيهام الأدمن بأنّ التبديل يؤثّر.
   */
  const deviceActive = device === "mobile";

  React.useEffect(() => {
    if (search.device) setDevice(search.device);
  }, [search.device]);

  // hydrate draft when both data sources are ready
  React.useEffect(() => {
    if (!collections || !config) return;
    const all = [...collections.map((c) => c.handle), ...VIRTUAL_HANDLES];
    const baseOrder = mergeOrder(config.order, all);
    const desktopOrder = mergeOrder(
      config.desktop_order.length > 0 ? config.desktop_order : baseOrder,
      all,
    );
    const mobileOrder = mergeOrder(
      config.mobile_order.length > 0 ? config.mobile_order : baseOrder,
      all,
    );
    setDraft({
      hidden: config.hidden.filter((h) => all.includes(h)),
      hidden_desktop: config.hidden_desktop.filter((h) => all.includes(h)),
      hidden_mobile: config.hidden_mobile.filter((h) => all.includes(h)),
      order: baseOrder,
      desktop_order: desktopOrder,
      mobile_order: mobileOrder,
      media_overrides: config.media_overrides ?? {},
    });
  }, [collections, config]);

  const isDirty = React.useMemo(() => {
    if (!draft || !config) return false;
    const sameArr = (a: string[], b: string[]) =>
      JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
    const sameHiddenDesktop = sameArr(draft.hidden_desktop, config.hidden_desktop);
    const sameHiddenMobile = sameArr(draft.hidden_mobile, config.hidden_mobile);
    const savedDesktop =
      config.desktop_order.length > 0 ? config.desktop_order : config.order;
    const savedMobile =
      config.mobile_order.length > 0 ? config.mobile_order : config.order;
    const desktopChanged = JSON.stringify(draft.desktop_order) !== JSON.stringify(savedDesktop);
    const mobileChanged = JSON.stringify(draft.mobile_order) !== JSON.stringify(savedMobile);
    const mediaChanged = JSON.stringify(draft.media_overrides) !== JSON.stringify(config.media_overrides ?? {});
    return !sameHiddenDesktop || !sameHiddenMobile || desktopChanged || mobileChanged || mediaChanged;
  }, [draft, config]);

  const collectionsMap = React.useMemo(() => {
    const m = new Map<string, { title: string; handle: string; imageUrl: string | null; virtual?: boolean }>();
    for (const c of collections ?? []) {
      m.set(c.handle, {
        title: c.title,
        handle: c.handle,
        imageUrl: c.image?.url ?? null,
      });
    }
    for (const key of VIRTUAL_HANDLES) {
      m.set(key, {
        title: VIRTUAL_HOME_BLOCK_LABELS[key],
        handle: key,
        imageUrl: null,
        virtual: true,
      });
    }
    return m;
  }, [collections]);

  React.useEffect(() => {
    if (!search.edit || !draft || !collectionsMap.has(search.edit)) return;
    if (openedSearchEditRef.current === search.edit) return;
    openedSearchEditRef.current = search.edit;
    setEditingMediaHandle(search.edit);
  }, [collectionsMap, draft, search.edit]);

  function move(handle: string, dir: -1 | 1) {
    if (!draft) return;
    const key: "desktop_order" | "mobile_order" =
      device === "desktop" ? "desktop_order" : "mobile_order";
    const arr = draft[key];
    const idx = arr.indexOf(handle);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= arr.length) return;
    const newArr = [...arr];
    [newArr[idx], newArr[next]] = [newArr[next], newArr[idx]];
    setDraft({ ...draft, [key]: newArr });
  }

  function toggleHidden(handle: string, hide: boolean) {
    if (!draft) return;
    const key: "hidden_desktop" | "hidden_mobile" =
      device === "desktop" ? "hidden_desktop" : "hidden_mobile";
    const set = new Set(draft[key]);
    if (hide) set.add(handle);
    else set.delete(handle);
    setDraft({ ...draft, [key]: Array.from(set) });
  }

  async function onSave() {
    if (!draft || !isDirty) return;
    setSaving(true);
    try {
      const oldUrls = collectMediaUrls(config?.media_overrides ?? {});
      const newUrls = collectMediaUrls(draft.media_overrides);
      const orphaned = [...oldUrls].filter((u) => !newUrls.has(u));

      await saveConfig({
        data: {
          hidden: draft.hidden,
          hidden_desktop: draft.hidden_desktop,
          hidden_mobile: draft.hidden_mobile,
          order: draft.order,
          desktop_order: draft.desktop_order,
          mobile_order: draft.mobile_order,
          media_overrides: draft.media_overrides,
        },
      });
      await qc.invalidateQueries({ queryKey: ["home-collections-config"] });
      await qc.invalidateQueries({ queryKey: ["home-collections-config", "admin"] });
      toast.success("تمّ الحفظ");

      // حذف الملفّات القديمة (fire-and-forget — لا يُفشل الحفظ)
      for (const url of orphaned) {
        deleteMedia({ data: { url } }).catch(() => {
          /* تجاهل بصمت */
        });
      }
    } catch (e) {
      toast.error("فشل الحفظ", { description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function onReset() {
    if (!config || !collections) return;
    const all = [...collections.map((c) => c.handle), ...VIRTUAL_HANDLES];
    const baseOrder = mergeOrder(config.order, all);
    const desktopOrder = mergeOrder(
      config.desktop_order.length > 0 ? config.desktop_order : baseOrder,
      all,
    );
    const mobileOrder = mergeOrder(
      config.mobile_order.length > 0 ? config.mobile_order : baseOrder,
      all,
    );
    setDraft({
      hidden: config.hidden.filter((h) => all.includes(h)),
      hidden_desktop: config.hidden_desktop.filter((h) => all.includes(h)),
      hidden_mobile: config.hidden_mobile.filter((h) => all.includes(h)),
      order: baseOrder,
      desktop_order: desktopOrder,
      mobile_order: mobileOrder,
      media_overrides: config.media_overrides ?? {},
    });
  }

  // نسخ ترتيب الديسكتوب إلى الموبايل (أو العكس)
  function copyOrderFrom(from: Device) {
    if (!draft) return;
    if (from === "desktop") {
      setDraft({ ...draft, mobile_order: [...draft.desktop_order] });
      toast.success("نُسخ ترتيب الديسكتوب إلى الموبايل");
    } else {
      setDraft({ ...draft, desktop_order: [...draft.mobile_order] });
      toast.success("نُسخ ترتيب الموبايل إلى الديسكتوب");
    }
  }

  const loading = cLoading || gLoading || !draft;
  const activeOrder = draft
    ? device === "desktop"
      ? draft.desktop_order
      : draft.mobile_order
    : [];

  return (
    <SiteShell>
      <AdminNav />
      <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <Link
              to="/admin"
              className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> العودة للأدمن
            </Link>
            <h1 className="text-2xl font-semibold">بلوكات الهوم — Collections</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              تحكّم بإظهار/إخفاء وترتيب المجموعات في الصفحة الرئيسيّة.
              <span className="block mt-1">
                لكلّ جهاز (ديسكتوب / موبايل) إعدادات إظهار وترتيب مستقلّة.
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              disabled={!isDirty || saving || !deviceActive}
            >
              <RotateCcw className="me-1.5 h-3.5 w-3.5" /> تراجع
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={!isDirty || saving || !deviceActive}
              className={isDirty && deviceActive ? "ring-2 ring-primary/40" : ""}
            >
              {saving ? (
                <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="me-1.5 h-3.5 w-3.5" />
              )}
              حفظ
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <Tabs value={device} onValueChange={(v) => setDevice(v as Device)}>
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <TabsList>
                <TabsTrigger value="desktop" className="gap-1.5">
                  <Monitor className="h-3.5 w-3.5" /> ديسكتوب
                </TabsTrigger>
                <TabsTrigger value="mobile" className="gap-1.5">
                  <Smartphone className="h-3.5 w-3.5" /> موبايل
                </TabsTrigger>
              </TabsList>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyOrderFrom(device === "desktop" ? "mobile" : "desktop")}
                className="text-xs"
                disabled={!deviceActive}
              >
                {device === "desktop"
                  ? "← نسخ ترتيب الموبايل إلى هنا"
                  : "← نسخ ترتيب الديسكتوب إلى هنا"}
              </Button>
            </div>

            <TabsContent value={device} className="m-0">
              {!deviceActive && (
                <div className="mb-4 flex items-start gap-3 border border-destructive/40 bg-destructive/5 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-destructive">
                      {t("admin.homeBlocks.deviceInactiveTitle")}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {t("admin.homeBlocks.deviceInactiveBody")}
                    </p>
                  </div>
                </div>
              )}
              <BlocksList
                order={activeOrder}
                hidden={device === "desktop" ? draft!.hidden_desktop : draft!.hidden_mobile}
                collectionsMap={collectionsMap}
                onMove={move}
                onToggleHidden={toggleHidden}
                onEditMedia={setEditingMediaHandle}
                mediaOverrides={draft!.media_overrides}
                device={device}
                deviceActive={deviceActive}
                noEffectLabel={t("admin.homeBlocks.noEffectBadge")}
              />
            </TabsContent>
          </Tabs>
        )}

        {draft && (
          <MediaEditorDialog
            saving={saving}
            onSave={async () => {
              await onSave();
              setEditingMediaHandle(null);
            }}
            open={!!editingMediaHandle}
            onOpenChange={(v) => !v && setEditingMediaHandle(null)}
            handle={editingMediaHandle}
            title={editingMediaHandle ? collectionsMap.get(editingMediaHandle)?.title || editingMediaHandle : ""}
            override={editingMediaHandle ? draft.media_overrides[editingMediaHandle] ?? {} : {}}
            onChange={(next) => {
              if (!editingMediaHandle) return;
              setDraft((prev) => {
                if (!prev) return prev;
                const copy = { ...prev.media_overrides };
                if (next.use_custom_media) {
                  copy[editingMediaHandle] = next;
                } else {
                  delete copy[editingMediaHandle];
                }
                return { ...prev, media_overrides: copy };
              });
            }}
          />
        )}

        <p className="mt-4 text-xs text-muted-foreground">
          ملاحظة: المجموعات التي لا تحوي صورة/فيديو/معرض لن تظهر في الهوم حتى لو كانت مفعَّلة هنا.
        </p>
      </div>
    </SiteShell>
  );
}

function BlocksList({
  order,
  hidden,
  collectionsMap,
  onMove,
  onToggleHidden,
  onEditMedia,
  mediaOverrides,
  device,
  deviceActive,
  noEffectLabel,
}: {
  order: string[];
  hidden: string[];
  collectionsMap: Map<string, { title: string; handle: string; imageUrl: string | null; virtual?: boolean }>;
  onMove: (handle: string, dir: -1 | 1) => void;
  onToggleHidden: (handle: string, hide: boolean) => void;
  onEditMedia: (handle: string) => void;
  mediaOverrides: Record<string, HomeBlockMediaOverride>;
  device: Device;
  /** الجهاز الحاليّ فعّال في الواجهة؟ عند false نعطّل كلّ التفاعل ونوسم الصفوف. */
  deviceActive: boolean;
  noEffectLabel: string;
}) {
  return (
    <ul className="divide-y rounded-none border bg-card">
      {order.map((handle, i) => {
        const c = collectionsMap.get(handle);
        if (!c) return null;
        const isHidden = hidden.includes(handle);
        const isFirst = i === 0;
        const isLast = i === order.length - 1;
        // صفّ بلا أثر: إمّا الجهاز كلّه غير فعّال، أو بطاقات بلوك المعلومات على الديسكتوب
        const isInfoBlock =
          handle === HOME_BLOCK_INFO_PRIMARY || handle === HOME_BLOCK_INFO_SECONDARY;
        const rowInactive = !deviceActive || (device === "desktop" && isInfoBlock);
        return (
          <li
            key={handle}
            className={`flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-3 ${isHidden || rowInactive ? "opacity-50" : ""}`}
          >
            <div className="flex items-center gap-2">
              <span className="w-5 text-center text-xs tabular-nums text-muted-foreground sm:w-6">
                {i + 1}
              </span>

              <div className="flex gap-1 sm:flex-col sm:gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-primary/30 bg-background hover:bg-primary/10 sm:h-9 sm:w-9"
                  disabled={isFirst || !deviceActive}
                  onClick={() => onMove(handle, -1)}
                  aria-label="تحريك للأعلى"
                  title="تحريك للأعلى"
                >
                  <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-primary/30 bg-background hover:bg-primary/10 sm:h-9 sm:w-9"
                  disabled={isLast || !deviceActive}
                  onClick={() => onMove(handle, 1)}
                  aria-label="تحريك للأسفل"
                  title="تحريك للأسفل"
                >
                  <ArrowDown className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>

              {(() => {
                const override = mediaOverrides[handle];
                const previewUrl =
                  override?.use_custom_media && override?.desktop_image_url
                    ? override.desktop_image_url
                    : c.imageUrl;
                return previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={c.title}
                    className="h-10 w-10 rounded object-cover sm:h-12 sm:w-12"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded text-[10px] font-medium sm:h-12 sm:w-12 ${c.virtual ? "bg-primary/10 text-primary" : "bg-muted"}`}
                  >
                    {c.virtual ? "★" : null}
                  </div>
                );
              })()}

              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-sm sm:text-base">{c.title}</p>
                <p className="truncate text-[11px] text-muted-foreground sm:text-xs">
                  {c.virtual
                    ? VIRTUAL_HOME_BLOCK_HINTS[c.handle as keyof typeof VIRTUAL_HOME_BLOCK_HINTS] ??
                      "بلوك داخليّ"
                    : c.handle}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 ps-16 sm:ps-0">
              <div className="flex flex-wrap items-center gap-1.5">
                {c.virtual && (
                  <Badge variant="outline" className="text-[10px]">
                    بلوك داخليّ
                  </Badge>
                )}
                {isHidden && (
                  <Badge variant="secondary" className="text-[10px]">
                    مخفيّ
                  </Badge>
                )}
                {rowInactive && (
                  <Badge variant="destructive" className="text-[10px]">
                    {noEffectLabel}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!c.virtual && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditMedia(handle)}
                      className="h-8 gap-1.5 text-xs"
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      {mediaOverrides[handle]?.use_custom_media ? "تعديل الوسائط" : "إضافة وسائط"}
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 text-xs"
                    >
                      <Link
                        to="/admin/collection-overlays"
                        search={{ from: "home-blocks", handle, device } as never}
                      >
                        النصّ والزرّ
                      </Link>
                    </Button>
                  </>
                )}
                <span className="text-xs text-muted-foreground">إظهار</span>
                <Switch
                  checked={!isHidden}
                  onCheckedChange={(v) => onToggleHidden(handle, !v)}
                  aria-label="إظهار/إخفاء البلوك"
                  disabled={!deviceActive}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function MediaEditorDialog({
  open,
  onOpenChange,
  handle,
  title,
  override,
  onChange,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handle: string | null;
  title: string;
  override: HomeBlockMediaOverride;
  onChange: (override: HomeBlockMediaOverride) => void;
  onSave: () => void | Promise<void>;
  saving: boolean;
}) {
  const { t } = useLocale();
  const [uploading, setUploading] = React.useState<"desktop" | "mobile" | null>(null);

  async function uploadImage(file: File, field: "desktop_image_url" | "mobile_image_url") {
    if (!handle) return;
    setUploading(field === "desktop_image_url" ? "desktop" : "mobile");
    try {
      const compressed = await compressImageBeforeUpload(file);
      const ext = compressed.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `home-block-media/${handle}/${field === "desktop_image_url" ? "desktop" : "mobile"}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("public-media").upload(path, compressed, {
        cacheControl: "3600",
        upsert: false,
        contentType: compressed.type,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("public-media").getPublicUrl(path);
      onChange({ ...override, [field]: bustUrl(data.publicUrl), use_custom_media: true });
      toast.success(t("admin.homeBlocks.uploadSuccess") ?? "تمّ رفع الصورة");
    } catch (e) {
      toast.error(t("admin.homeBlocks.uploadError") ?? "فشل رفع الصورة", {
        description: (e as Error).message,
      });
    } finally {
      setUploading(null);
    }
  }

  function clearField(field: "desktop_image_url" | "mobile_image_url" | "video_url") {
    const next = { ...override };
    delete next[field];
    const hasAny = !!(next.desktop_image_url || next.mobile_image_url || next.video_url);
    onChange({ ...next, use_custom_media: hasAny });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle>{t("admin.homeBlocks.mediaDialogTitle").replace("{title}", title)}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t("admin.homeBlocks.mediaDialogHint")}</p>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-none border p-3">
            <div>
              <p className="font-medium text-sm">{t("admin.homeBlocks.useCustomMedia")}</p>
              <p className="text-xs text-muted-foreground">فعّل لتجاوز صورة/فيديو Shopify لهذه المجموعة.</p>
            </div>
            <Switch
              checked={!!override.use_custom_media}
              onCheckedChange={(v) => onChange({ ...override, use_custom_media: !!v })}
            />
          </div>

          <ImageField
            label={t("admin.homeBlocks.desktopImage")}
            url={override.desktop_image_url}
            uploading={uploading === "desktop"}
            onFile={(file) => uploadImage(file, "desktop_image_url")}
            onClear={() => clearField("desktop_image_url")}
          />
          <ImageField
            label={t("admin.homeBlocks.mobileImage")}
            url={override.mobile_image_url}
            uploading={uploading === "mobile"}
            onFile={(file) => uploadImage(file, "mobile_image_url")}
            onClear={() => clearField("mobile_image_url")}
          />

          <div className="space-y-2">
            <Label>{t("admin.homeBlocks.videoUrl")}</Label>
            <div className="flex items-center gap-2">
              <Input
                value={override.video_url || ""}
                onChange={(e) =>
                  onChange({
                    ...override,
                    video_url: e.target.value || undefined,
                    use_custom_media: true,
                  })
                }
                placeholder="https://..."
                dir="ltr"
              />
              {override.video_url && (
                <Button variant="ghost" size="icon" onClick={() => clearField("video_url")}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-start">
          <Button onClick={() => onSave()} disabled={saving} className="min-w-24">
            {saving ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="me-1.5 h-3.5 w-3.5" />}
            حفظ
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            إغلاق
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageField({
  label,
  url,
  uploading,
  onFile,
  onClear,
}: {
  label: string;
  url?: string | null;
  uploading: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}) {
  const { t } = useLocale();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  function handleDrop(e: React.DragEvent<HTMLButtonElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = "dataTransfer" in e ? e.dataTransfer.files : null;
    const file = files?.[0];
    if (file && file.type.startsWith("image/")) {
      onFile(file);
    } else if (file) {
      toast.error(t("admin.homeBlocks.uploadError") ?? "فشل رفع الصورة", {
        description: t("admin.homeBlocks.dropImageOnly") ?? "يرجى إفلات ملف صورة فقط.",
      });
    }
  }

  function handleDragOver(e: React.DragEvent<HTMLButtonElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLButtonElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }

  const dropZoneClass =
    "relative flex h-32 w-full flex-col items-center justify-center gap-2 rounded-none border-2 border-dashed text-sm text-muted-foreground transition-colors";
  const dropZoneActive = isDragging
    ? "border-primary bg-primary/5 text-primary"
    : "border-border hover:bg-muted/50";

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {url ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`${dropZoneClass} ${dropZoneActive} overflow-hidden p-0`}
        >
          <img src={url} alt={label} className="h-full w-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity hover:opacity-100">
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="h-9 w-9"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              aria-label={t("admin.homeBlocks.removeImage") ?? "إزالة الصورة"}
              title={t("admin.homeBlocks.removeImage") ?? "إزالة الصورة"}
            >
              <X className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              className="gap-1.5"
            >
              <UploadCloud className="h-4 w-4" />
              {t("admin.homeBlocks.replaceImage") ?? "استبدال"}
            </Button>
          </div>
          {isDragging && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 text-primary">
              <span className="font-medium">{t("admin.homeBlocks.dropHere") ?? "أفلت الصورة هنا"}</span>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`${dropZoneClass} ${dropZoneActive} disabled:opacity-50`}
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <UploadCloud className={`h-6 w-6 ${isDragging ? "text-primary" : ""}`} />
          )}
          <span className="text-center leading-relaxed">
            {uploading
              ? t("admin.homeBlocks.uploading") ?? "جاري الرفع..."
              : t("admin.homeBlocks.dragDropOrClick") ?? "اسحب صورة هنا أو اضغط لرفعها"}
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
